import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import {
	RevealBar,
	REVEAL_BAR_DURATION_MS,
	REVEAL_STAGGER_MS,
} from "../RevealBar";
import { plainLanguageRevealSummary } from "../../lib/revealSummary";
import type { FinalResults } from "../../types/finalResults";

type ResultsBoardProps = {
	results: FinalResults;
	/**
	 * PRD §6.5 — stagger bar reveal only on Finalize/Reveal this session.
	 * Never pass true on cold load of an already-revealed room.
	 */
	animateReveal?: boolean;
};

export function ResultsBoard({ results, animateReveal = false }: ResultsBoardProps) {
	const reduceMotion = useReducedMotion() === true;
	const playReveal = animateReveal && !reduceMotion;
	const sorted = [...results.optionTallies].sort((a, b) => b.normalized - a.normalized);
	const [showSummary, setShowSummary] = useState(!playReveal);
	const summary = plainLanguageRevealSummary(results);

	useEffect(() => {
		if (!playReveal || sorted.length === 0) {
			setShowSummary(true);
			return;
		}
		setShowSummary(false);
		const totalMs =
			Math.max(0, sorted.length - 1) * REVEAL_STAGGER_MS + REVEAL_BAR_DURATION_MS;
		const summaryT = window.setTimeout(() => setShowSummary(true), totalMs + 200);
		return () => {
			window.clearTimeout(summaryT);
		};
	}, [playReveal, sorted.length]);

	return (
		<div className="space-y-md">
			<div>
				<p className="font-medium text-body-sm text-wi-text">
					Outcome
					<span className="ml-2 font-normal text-wi-text-muted">
						({results.voteCount} vote{results.voteCount === 1 ? "" : "s"})
					</span>
				</p>
				{results.voteCount === 0 ? (
					<p className="mt-2 text-body-sm text-wi-text-secondary">
						No votes were recorded before this poll was finalized.
					</p>
				) : null}
				{summary && showSummary ? (
					<motion.p
						className="mt-sm text-body text-wi-signal"
						initial={playReveal && !reduceMotion ? { opacity: 0, y: 4 } : false}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: reduceMotion ? 0 : 0.2, ease: [0.22, 1, 0.36, 1] }}
					>
						{summary}
					</motion.p>
				) : null}
			</div>

			{results.voteCount > 0 ? (
				<div className="space-y-0 pt-xs" aria-label="Final ranking">
					{sorted.map((t, i) => {
						const win = results.winners.includes(t.id);
						return (
							<RevealBar
								key={t.id}
								label={t.content}
								value={t.normalized}
								isWinner={win}
								delayMs={i * REVEAL_STAGGER_MS}
								animate={playReveal}
							/>
						);
					})}
				</div>
			) : null}

			<details className="group pt-xs text-body-sm text-wi-text-secondary">
				<summary className="wi-link-draw cursor-pointer list-none font-medium text-wi-gold [&::-webkit-details-marker]:hidden">
					Show the math
				</summary>
				<div className="mt-sm space-y-sm border-wi-border border-t pt-sm">
					<ul className="space-y-xs font-mono text-caption text-wi-text-muted" aria-label="Scores">
						{sorted.map((t) => (
							<li key={t.id} className="flex flex-wrap items-baseline justify-between gap-2">
								<span className="font-sans font-medium text-body-sm text-wi-text-secondary">
									{t.content}
								</span>
								<span>
									score {t.score.toFixed(1)} · norm {t.normalized.toFixed(0)}
								</span>
							</li>
						))}
					</ul>
					{results.tieBands.length > 0 ? (
						<p className="text-caption text-wi-text-muted">
							Tight outcome bands (&le;2 pts apart once normalized) were flagged in the tally.
						</p>
					) : null}
						<p className="text-caption text-wi-text-muted leading-relaxed">
							Style:{" "}
							{results.votingType === "ranked"
								? "Ranked choice (Borda count)"
								: "Weighted picks (quadratic voting)"}
							.{" "}
							{results.votingType === "ranked"
								? "Scores sum Borda points from each ballot; finalists are whoever shares the highest total."
								: "Scores sum each voter’s allocated points per idea (Σ votes). Quadratic budget (Σ v²) was enforced when people cast ballots."}
						</p>
				</div>
			</details>
		</div>
	);
}
